//CONSTANT
import { GET_APP_NAME_FOR_COLLECTION } from "../../app_constant";

//PACKAGES
import { auth, firestore } from "./config";
import { getEffectiveUid } from "./ghostUid";

const collection = async () => {
  const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
  return firestore()
    .collection("apps")
    .doc(APP_NAME)
    .collection("users")
    .doc(getEffectiveUid());
};

async function SAVE_ARTICLE_BOOKMARK(data) {
  try {
    const collection_user = await collection();
    await collection_user.update({
      article_bookmarks: data,
    });
    return { status: true, error: "" };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function SAVE_ARTICLE_PROGRESS(data) {
  try {
    const collection_user = await collection();
    await collection_user.update({
      articles_progress: data,
    });
    return { status: true, error: "" };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function SAVE_ARTICLE_SUMMARY(data) {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");
    
    // Store summary as a separate document in articles subcollection
    await articlesCollection.doc("article_summary").set({
      ...data,
      last_updated: new Date().toISOString()
    });
    
    return { status: true, error: "" };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function GET_ARTICLE_SUMMARY() {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");
    
    const summaryDoc = await articlesCollection.doc("article_summary").get();
    const summaryData = summaryDoc.exists ? summaryDoc.data() : null;
    
    return { 
      status: true, 
      error: "",
      data: summaryData
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function MARK_ARTICLE_AS_READ(articleId, articleData, currentSummary = null) {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");
    
    const now = new Date().toISOString();
    const { topic_id } = articleData || {};
    console.log("topic_id", topic_id);
    console.log("articleData", articleData);
    console.log("articleId", articleId);
    
    // Validate articleId
    if (!articleId) {
      return { status: false, error: "articleId is required" };
    }
    
    // Ensure articleId is a string
    const stringArticleId = String(articleId);
    if (!stringArticleId.trim()) {
      return { status: false, error: "articleId must be a valid string" };
    }
    
    // Validate topic_id
    if (!topic_id) {
      return { status: false, error: "topic_id is required" };
    }
    
    // Get current article document - defensive read to preserve existing data
    console.log("About to fetch article document for articleId:", stringArticleId);
    let articleDoc;
    let existingArticle = null;
    try {
      articleDoc = await articlesCollection.doc(stringArticleId).get();
      console.log("articleDoc exists:", articleDoc.exists);
      
      if (articleDoc.exists) {
        existingArticle = articleDoc.data();
        console.log("existingArticle", existingArticle);
        // Log warning if reaction exists but might be lost
        if (existingArticle?.reaction && !existingArticle?.read) {
          console.warn(`[MARK_ARTICLE_AS_READ] Article ${stringArticleId} has reaction (${existingArticle.reaction}) but hasn't been marked as read yet. Preserving reaction.`);
        }
      } else {
        console.log("Article document does not exist yet, will create new document");
      }
    } catch (fetchError) {
      console.error("Error fetching article document:", fetchError);
      // Don't fail completely - we'll still try to save, but log the error
      console.warn(`[MARK_ARTICLE_AS_READ] Failed to read existing article document for ${stringArticleId}. Proceeding with merge to preserve any existing data.`);
    }
    
    const isNewRead = !existingArticle;
    console.log("isNewRead", isNewRead);
    
    // Update article record - preserve reaction fields and all other existing fields
    const updatedArticleData = {
      read: true,
      first_read_at: existingArticle?.first_read_at || now,
      last_read_at: now,
      read_count: (existingArticle?.read_count || 0) + 1,
      topic_id,
      // Explicitly preserve reaction fields if they exist
      reaction: existingArticle?.reaction || null,
      reacted_at: existingArticle?.reacted_at || null
    };
    
    // Log reaction preservation status
    if (existingArticle?.reaction) {
      console.log(`[MARK_ARTICLE_AS_READ] Preserving existing reaction: ${existingArticle.reaction} for article ${stringArticleId}`);
    }
    
    // Save article document with merge option to preserve all existing fields
    console.log("About to save article document with merge option");
    try {
      // Use merge: true to preserve existing fields that aren't explicitly updated
      // This ensures reaction and other fields aren't lost even if document read failed
      await articlesCollection.doc(stringArticleId).set(updatedArticleData, { merge: true });
      console.log("Successfully saved article document with merge");
      
      // Verify reaction was preserved after save
      if (existingArticle?.reaction) {
        const verifyDoc = await articlesCollection.doc(stringArticleId).get();
        const savedData = verifyDoc.exists ? verifyDoc.data() : null;
        if (savedData?.reaction !== existingArticle.reaction) {
          console.error(`[MARK_ARTICLE_AS_READ] WARNING: Reaction may have been lost! Original: ${existingArticle.reaction}, Saved: ${savedData?.reaction || 'null'}`);
        } else {
          console.log(`[MARK_ARTICLE_AS_READ] Verified reaction preserved: ${savedData.reaction}`);
        }
      }
    } catch (articleSaveError) {
      console.error("Error saving article document:", articleSaveError);
      return { status: false, error: `Failed to save article: ${articleSaveError.message}` };
    }

    // Update global article read count every time an article is read
    try {
      console.log(`[MARK_ARTICLE_AS_READ] Calling UPDATE_GLOBAL_ARTICLE_READ_COUNT for article ${stringArticleId}`);
      await UPDATE_GLOBAL_ARTICLE_READ_COUNT(stringArticleId);
      console.log(`[MARK_ARTICLE_AS_READ] Successfully updated global article read count for ${stringArticleId}`);
    } catch (globalUpdateError) {
      console.error(`[MARK_ARTICLE_AS_READ] Error updating global article read count for ${stringArticleId}:`, globalUpdateError);
      // Non-critical - don't fail the operation
    }

    // Update summary stats - use provided currentSummary from state if available, otherwise read from Firestore
    let currentSummaryData;
    if (currentSummary) {
      // Use summary from state (optimization: avoids 1 read operation)
      console.log("Using summary from state");
      currentSummaryData = currentSummary;
    } else {
      // Fallback: read from Firestore if summary not provided
      console.log("About to fetch article summary document");
      let summaryDoc;
      try {
        summaryDoc = await articlesCollection.doc("article_read_summary").get();
        console.log("summaryDoc", summaryDoc);
      } catch (summaryFetchError) {
        console.error("Error fetching article summary document:", summaryFetchError);
        return { status: false, error: `Failed to fetch article summary: ${summaryFetchError.message}` };
      }
      
      currentSummaryData = summaryDoc.exists ? summaryDoc.data() : {
        total_articles_read: 0,
        total_read_count: 0,
        last_read_at: null,
        articles_by_topic: {}
      };
    }
    console.log("currentSummary", currentSummaryData);
    
    const newSummary = {
      total_articles_read: isNewRead ? currentSummaryData.total_articles_read + 1 : currentSummaryData.total_articles_read,
      total_read_count: currentSummaryData.total_read_count + 1,
      last_read_at: now,
      articles_by_topic: { ...currentSummaryData.articles_by_topic }
    };
    
    // Update topic stats
    if (!newSummary.articles_by_topic[topic_id]) {
      newSummary.articles_by_topic[topic_id] = {
        read_count: 0,
        last_read_at: null
      };
    }
    
    if (isNewRead) {
      newSummary.articles_by_topic[topic_id].read_count += 1;
    }
    newSummary.articles_by_topic[topic_id].last_read_at = now;
    
    // Save summary to articles subcollection
    console.log("About to save summary to articles subcollection");
    try {
      await articlesCollection.doc("article_read_summary").set(newSummary);
      console.log("Successfully saved summary to articles subcollection");
    } catch (summaryUpdateError) {
      console.error("Error saving summary to articles subcollection:", summaryUpdateError);
      return { status: false, error: `Failed to save summary: ${summaryUpdateError.message}` };
    }
    
    return { 
      status: true, 
      error: "",
      data: {
        article: updatedArticleData,
        summary: newSummary
      }
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function GET_ARTICLE_READ_STATUS(articleId) {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");

    // Convert articleId to string to match how it's saved in other functions
    const stringArticleId = String(articleId);
    const articleDoc = await articlesCollection.doc(stringArticleId).get();
    const articleData = articleDoc.exists ? articleDoc.data() : null;

    return {
      status: true,
      error: "",
      data: articleData
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function GET_ARTICLE_READ_SUMMARY() {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");
    
    const summaryDoc = await articlesCollection.doc("article_read_summary").get();
    const summaryData = summaryDoc.exists ? summaryDoc.data() : {
      total_articles_read: 0,
      total_read_count: 0,
      last_read_at: null,
      articles_by_topic: {}
    };
    
    return { 
      status: true, 
      error: "",
      data: summaryData
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function GET_ARTICLE_BOOKMARKS() {
  try {
    const collection_user = await collection();
    const userDoc = await collection_user.get();
    let bookmarksData = userDoc.exists ? (userDoc.data().article_bookmarks || []) : [];

    // Migration-on-read: if user has no article_bookmarks in new location, check old location
    if ((!bookmarksData || bookmarksData.length === 0) && userDoc.exists) {
      const articlesCollection = collection_user.collection("articles");
      const oldBookmarksDoc = await articlesCollection.doc("article_bookmarks").get();
      if (oldBookmarksDoc.exists) {
        const migratedBookmarks = oldBookmarksDoc.data().bookmarks || [];
        if (migratedBookmarks.length > 0) {
          await collection_user.update({ article_bookmarks: migratedBookmarks });
          bookmarksData = migratedBookmarks;
        }
      }
    }

    return {
      status: true,
      error: "",
      data: bookmarksData,
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

async function GET_READ_ARTICLES_BY_TOPIC(topicId) {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");

    const articlesSnapshot = await articlesCollection
      .where("topic_id", "==", topicId)
      .get();

    const topicArticles = articlesSnapshot.docs.map(doc => ({
      article_id: doc.id,
      ...doc.data()
    }));

    return {
      status: true,
      error: "",
      data: topicArticles
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

/**
 * Update global article read count
 * Increments the read_count in /apps/{APP_NAME}/articles/{articleId}
 * Uses atomic FieldValue.increment() to prevent race conditions
 * @param {string} articleId - The article ID
 * @returns {Promise<{status: boolean, error: string}>}
 */
async function UPDATE_GLOBAL_ARTICLE_READ_COUNT(articleId) {
  try {
    const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
    const globalArticleRef = firestore()
      .collection("apps")
      .doc(APP_NAME)
      .collection("articles")
      .doc(String(articleId));

    console.log(`[UPDATE_GLOBAL_ARTICLE_READ_COUNT] Starting increment for article ${articleId}`);

    // Use atomic increment to avoid race conditions
    // Try update() first (more efficient), fall back to set() if document doesn't exist
    try {
      await globalArticleRef.update({
        read_count: firestore.FieldValue.increment(1),
        last_updated: new Date().toISOString()
      });
      console.log(`[UPDATE_GLOBAL_ARTICLE_READ_COUNT] Successfully incremented read_count for article ${articleId} using update()`);
    } catch (updateError) {
      // If document doesn't exist, create it with set()
      if (updateError.code === 'not-found' || updateError.code === 5) {
        console.log(`[UPDATE_GLOBAL_ARTICLE_READ_COUNT] Document doesn't exist, creating with set() for article ${articleId}`);
        await globalArticleRef.set({
          read_count: 1,
          like_count: 0,
          dislike_count: 0,
          last_updated: new Date().toISOString()
        });
        console.log(`[UPDATE_GLOBAL_ARTICLE_READ_COUNT] Successfully created document with read_count=1 for article ${articleId}`);
      } else {
        throw updateError;
      }
    }

    return { status: true, error: "" };
  } catch (error) {
    console.error(`[UPDATE_GLOBAL_ARTICLE_READ_COUNT] Error updating global article read count for ${articleId}:`, error);
    return { status: false, error: error.message };
  }
}

/**
 * Update global article statistics (like_count, dislike_count)
 * @param {string} articleId - The article ID
 * @param {string|null} oldReaction - Previous reaction ('like', 'dislike', or null)
 * @param {string|null} newReaction - New reaction ('like', 'dislike', or null)
 * @returns {Promise<{status: boolean, error: string}>}
 */
async function UPDATE_GLOBAL_ARTICLE_STATS(articleId, oldReaction, newReaction) {
  try {
    const APP_NAME = await GET_APP_NAME_FOR_COLLECTION();
    const globalArticleRef = firestore()
      .collection("apps")
      .doc(APP_NAME)
      .collection("articles")
      .doc(String(articleId));

    const doc = await globalArticleRef.get();
    const currentData = doc.exists ? doc.data() : {
      read_count: 0,
      like_count: 0,
      dislike_count: 0,
      last_updated: null
    };

    let { like_count = 0, dislike_count = 0 } = currentData;

    // Decrement old reaction count
    if (oldReaction === 'like') {
      like_count = Math.max(0, like_count - 1);
    } else if (oldReaction === 'dislike') {
      dislike_count = Math.max(0, dislike_count - 1);
    }

    // Increment new reaction count
    if (newReaction === 'like') {
      like_count += 1;
    } else if (newReaction === 'dislike') {
      dislike_count += 1;
    }

    const updatedStats = {
      ...currentData,
      like_count,
      dislike_count,
      last_updated: new Date().toISOString()
    };

    await globalArticleRef.set(updatedStats);

    return { status: true, error: "" };
  } catch (error) {
    console.error('Error updating global article stats:', error);
    return { status: false, error: error.message };
  }
}

/**
 * Update user's article reaction summary
 * @param {object} articlesCollection - User's articles subcollection reference
 * @param {string|null} oldReaction - Previous reaction ('like', 'dislike', or null)
 * @param {string|null} newReaction - New reaction ('like', 'dislike', or null)
 * @param {string} topicId - The topic ID of the article
 * @returns {Promise<{status: boolean, error: string}>}
 */
async function UPDATE_USER_REACTION_SUMMARY(articlesCollection, oldReaction, newReaction, topicId) {
  try {
    const summaryDoc = await articlesCollection.doc("article_reaction_summary").get();
    const currentSummary = summaryDoc.exists ? summaryDoc.data() : {
      total_articles_liked: 0,
      total_articles_disliked: 0,
      last_reaction_at: null,
      reactions_by_topic: {}
    };

    const now = new Date().toISOString();
    let { total_articles_liked = 0, total_articles_disliked = 0, reactions_by_topic = {} } = currentSummary;

    // Initialize topic stats if not exists
    if (topicId && !reactions_by_topic[topicId]) {
      reactions_by_topic[topicId] = {
        like_count: 0,
        dislike_count: 0,
        last_reaction_at: null
      };
    }

    // Update totals based on old reaction (decrement)
    if (oldReaction === 'like') {
      total_articles_liked = Math.max(0, total_articles_liked - 1);
      if (topicId && reactions_by_topic[topicId]) {
        reactions_by_topic[topicId].like_count = Math.max(0, reactions_by_topic[topicId].like_count - 1);
      }
    } else if (oldReaction === 'dislike') {
      total_articles_disliked = Math.max(0, total_articles_disliked - 1);
      if (topicId && reactions_by_topic[topicId]) {
        reactions_by_topic[topicId].dislike_count = Math.max(0, reactions_by_topic[topicId].dislike_count - 1);
      }
    }

    // Update totals based on new reaction (increment)
    if (newReaction === 'like') {
      total_articles_liked += 1;
      if (topicId && reactions_by_topic[topicId]) {
        reactions_by_topic[topicId].like_count += 1;
      }
    } else if (newReaction === 'dislike') {
      total_articles_disliked += 1;
      if (topicId && reactions_by_topic[topicId]) {
        reactions_by_topic[topicId].dislike_count += 1;
      }
    }

    // Update last reaction timestamp
    if (topicId && newReaction !== null && reactions_by_topic[topicId]) {
      reactions_by_topic[topicId].last_reaction_at = now;
    }

    const updatedSummary = {
      total_articles_liked,
      total_articles_disliked,
      last_reaction_at: newReaction !== null ? now : currentSummary.last_reaction_at,
      reactions_by_topic
    };

    await articlesCollection.doc("article_reaction_summary").set(updatedSummary);

    return { status: true, error: "" };
  } catch (error) {
    console.error('Error updating user reaction summary:', error);
    return { status: false, error: error.message };
  }
}

/**
 * Save user's reaction to an article
 * @param {string} articleId - The article ID
 * @param {string|null} reaction - 'like', 'dislike', or null to remove
 * @param {object} articleData - Additional article data (topic_id, etc.)
 * @returns {Promise<{status: boolean, error: string, data?: object}>}
 */
async function SAVE_ARTICLE_REACTION(articleId, reaction, articleData = {}) {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");

    // Validate articleId
    if (!articleId) {
      return { status: false, error: "articleId is required" };
    }

    const stringArticleId = String(articleId);
    if (!stringArticleId.trim()) {
      return { status: false, error: "articleId must be a valid string" };
    }

    // Validate reaction value
    if (reaction !== null && reaction !== 'like' && reaction !== 'dislike') {
      return { status: false, error: "reaction must be 'like', 'dislike', or null" };
    }

    const now = new Date().toISOString();

    // Get current article document to check existing reaction
    const articleDoc = await articlesCollection.doc(stringArticleId).get();
    const existingData = articleDoc.exists ? articleDoc.data() : {};
    const oldReaction = existingData.reaction || null;

    // Update user's article document with reaction
    const updatedData = {
      ...existingData,
      reaction: reaction,
      reacted_at: reaction !== null ? now : null,
      topic_id: articleData.topic_id || existingData.topic_id
    };

    // If article hasn't been read yet, mark it as read
    if (!existingData.read) {
      updatedData.read = true;
      updatedData.first_read_at = now;
      updatedData.last_read_at = now;
      updatedData.read_count = 1;
    }

    await articlesCollection.doc(stringArticleId).set(updatedData);

    // Update global article stats
    await UPDATE_GLOBAL_ARTICLE_STATS(stringArticleId, oldReaction, reaction);

    // Update user reaction summary
    await UPDATE_USER_REACTION_SUMMARY(articlesCollection, oldReaction, reaction, articleData.topic_id);

    return {
      status: true,
      error: "",
      data: {
        article: updatedData,
        oldReaction,
        newReaction: reaction
      }
    };
  } catch (error) {
    console.error('Error saving article reaction:', error);
    return { status: false, error: error.message };
  }
}

/**
 * Get user's article reaction summary
 * @returns {Promise<{status: boolean, error: string, data?: object}>}
 */
async function GET_ARTICLE_REACTION_SUMMARY() {
  try {
    const collection_user = await collection();
    const articlesCollection = collection_user.collection("articles");

    const summaryDoc = await articlesCollection.doc("article_reaction_summary").get();
    const summaryData = summaryDoc.exists ? summaryDoc.data() : {
      total_articles_liked: 0,
      total_articles_disliked: 0,
      last_reaction_at: null,
      reactions_by_topic: {}
    };

    return {
      status: true,
      error: "",
      data: summaryData
    };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

/**
 * Metered unlocks (grow-only map, see @learnzapp/core/articles).
 * Doc shape: { unlocks: { [articleId]: { period_key, unlocked_at, source } }, updated_at }
 * Reads come through the articles-subcollection listener in AuthProvider.
 */
async function SAVE_ARTICLE_UNLOCKS(unlocksMap) {
  try {
    const collection_user = await collection();
    // Merge-write of the whole map: it only grows, so last-writer-wins across
    // devices can grant an extra unlock but never revoke one.
    await collection_user.collection("articles").doc("article_unlocks").set({
      unlocks: unlocksMap,
      updated_at: new Date().toISOString()
    }, { merge: true });
    return { status: true, error: "" };
  } catch (error) {
    return { status: false, error: error.message };
  }
}

export {
  SAVE_ARTICLE_BOOKMARK,
  SAVE_ARTICLE_PROGRESS,
  SAVE_ARTICLE_SUMMARY,
  GET_ARTICLE_SUMMARY,
  GET_ARTICLE_BOOKMARKS,
  MARK_ARTICLE_AS_READ,
  GET_ARTICLE_READ_STATUS,
  GET_ARTICLE_READ_SUMMARY,
  GET_READ_ARTICLES_BY_TOPIC,
  SAVE_ARTICLE_UNLOCKS,
  SAVE_ARTICLE_REACTION,
  GET_ARTICLE_REACTION_SUMMARY,
  UPDATE_GLOBAL_ARTICLE_READ_COUNT
}; 